<template>
  <div class="p-4 space-y-4">
    <h2 class="text-lg font-bold">Camera & Microphone Access</h2>

    <!-- Permission Status -->
    <div>
      <p>
        Camera: <strong>{{ cameraStatus }}</strong>
      </p>
      <p>
        Microphone: <strong>{{ micStatus }}</strong>
      </p>
    </div>

    <!-- Buttons -->
    <div class="space-x-2">
      <button
        @click="checkPermissions"
        class="px-3 py-1 bg-blue-500 text-white rounded"
      >
        Check Permissions
      </button>
      <button
        @click="requestMedia"
        class="px-3 py-1 bg-green-500 text-white rounded"
      >
        Enable Camera & Mic
      </button>
    </div>

    <!-- Video Stream -->
    <video
      v-if="stream"
      ref="videoRef"
      autoplay
      playsinline
      class="border border-gray-300 rounded w-80 h-60"
    ></video>

    <!-- Error Message -->
    <p v-if="errorMessage" class="text-red-500">{{ errorMessage }}</p>

    <!-- Guide for denied permissions -->
    <div
      v-if="cameraStatus === 'denied' || micStatus === 'denied'"
      class="text-red-600 border p-2 rounded bg-red-50"
    >
      Camera or Microphone is blocked. <br />
      Please enable it manually in your browser settings. <br />
      <button
        @click="showHelp"
        class="mt-2 px-2 py-1 bg-red-500 text-white rounded"
      >
        How to Enable
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";

const cameraStatus = ref("unknown");
const micStatus = ref("unknown");
const stream = ref(null);
const videoRef = ref(null);
const errorMessage = ref("");

// Check permission status
const checkPermissions = async () => {
  if (!navigator.permissions) {
    errorMessage.value = "Permissions API not supported in this browser.";
    return;
  }

  try {
    const camera = await navigator.permissions.query({ name: "camera" });
    const mic = await navigator.permissions.query({ name: "microphone" });

    cameraStatus.value = camera.state;
    micStatus.value = mic.state;

    camera.onchange = () => (cameraStatus.value = camera.state);
    mic.onchange = () => (micStatus.value = mic.state);
  } catch (err) {
    errorMessage.value = "Error checking permissions: " + err.message;
  }
};

// Request media access
const requestMedia = async () => {
  try {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    stream.value = mediaStream;
    errorMessage.value = "";
  } catch (err) {
    errorMessage.value = "Access denied or error: " + err.message;
  }
};

// Attach stream to video element
watch(stream, (newStream) => {
  if (videoRef.value && newStream) {
    videoRef.value.srcObject = newStream;
  }
});

// Show guide to re-enable permissions
const showHelp = () => {
  alert(
    "To re-enable camera/mic access:\n\n" +
      "Chrome / Edge / Brave:\n" +
      "1. Click the lock icon next to the URL.\n" +
      "2. Go to Site settings.\n" +
      "3. Allow Camera and Microphone.\n" +
      "4. Refresh the page.\n\n" +
      "Firefox:\n" +
      "1. Click the lock icon next to the URL.\n" +
      "2. Click Permissions.\n" +
      "3. Allow Camera and Microphone.\n" +
      "4. Refresh the page.\n\n" +
      "Safari:\n" +
      "1. Safari menu → Settings for This Website.\n" +
      "2. Allow Camera and Microphone.\n" +
      "3. Refresh the page."
  );
};

// Auto check permissions on mount
onMounted(() => {
  checkPermissions();
});
</script>

<style scoped>
button {
  cursor: pointer;
}
</style>
